/**
 * Convomesh Social Proof Widget
 *
 * Fetches personalised recommendation reasons from the Convomesh storefront API
 * and renders them as dot points on Shopify PDPs. Reasons are computed server-side
 * using the full buildRecommendationReasons engine (person-fit copy, social proof,
 * reviews, stock, pricing).
 *
 * Re-fetches with updated person preferences when the sizing wizard fires
 * the convomesh-person-updated event.
 */
(function() {
  'use strict';

  var STORAGE_KEY = 'convomesh_person';
  var DEBOUNCE_MS = 400;

  // ── State ──

  var targetEl = null;
  var tenantId = null;
  var handle = null;
  var currentPersonPrefs = {};
  var debounceTimer = null;

  // ── Render ──

  function render(reasons) {
    if (!targetEl) return;
    if (!reasons || reasons.length === 0) {
      targetEl.style.display = 'none';
      return;
    }

    // Clear previous content
    targetEl.innerHTML = '';

    for (var i = 0; i < reasons.length; i++) {
      var li = document.createElement('li');
      li.style.cssText = 'padding: 2px 0; list-style: none; display: flex; align-items: baseline; gap: 6px;';
      var tick = document.createElement('span');
      tick.textContent = '\u2713';
      tick.style.cssText = 'color: #3D9A8B; font-weight: 700; flex-shrink: 0;';
      var text = document.createElement('span');
      text.textContent = reasons[i];
      li.appendChild(tick);
      li.appendChild(text);
      targetEl.appendChild(li);
    }

    targetEl.style.display = 'block';
  }

  // ── Fetch ──

  function fetchReasons() {
    if (!tenantId || !handle) return;

    var storeDomain = tenantId + '.myshopishop.com';
    var url = 'https://' + storeDomain + '/api/social-proof?tenantId=' +
      encodeURIComponent(tenantId) + '&handle=' + encodeURIComponent(handle);

    // Pass person preferences if available
    if (currentPersonPrefs && Object.keys(currentPersonPrefs).length > 0) {
      url += '&person=' + encodeURIComponent(JSON.stringify(currentPersonPrefs));
    }

    fetch(url)
      .then(function(r) { return r.json(); })
      .then(function(data) {
        if (data.ok && data.reasons && data.reasons.length > 0) {
          render(data.reasons);
        } else if (data.ok) {
          render([]);
        }
      })
      .catch(function() { /* silent */ });
  }

  function debouncedFetch() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(fetchReasons, DEBOUNCE_MS);
  }

  // ── Load stored person prefs ──

  function loadStoredPrefs() {
    try {
      var raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return {};
      var stored = JSON.parse(raw);
      return stored.preferences || {};
    } catch (e) {
      return {};
    }
  }

  // ── Init ──

  function init() {
    targetEl = document.getElementById('convomesh-social-proof-text');
    if (!targetEl) return;

    tenantId = targetEl.getAttribute('data-tenant');
    handle = targetEl.getAttribute('data-handle');
    if (!tenantId || !handle) return;

    // Load any existing person prefs from sessionStorage
    currentPersonPrefs = loadStoredPrefs();

    // Initial fetch
    fetchReasons();

    // Listen for sizing wizard updates (live preview + final commit)
    window.addEventListener('convomesh-person-updated', function(event) {
      var detail = event.detail || {};
      var prefs = detail.preferences || {};
      var changed = false;

      // Merge new prefs into current
      for (var key in prefs) {
        if (prefs.hasOwnProperty(key) && prefs[key] !== currentPersonPrefs[key]) {
          currentPersonPrefs[key] = prefs[key];
          changed = true;
        }
      }

      if (changed) {
        // Re-fetch with updated person prefs (debounced to avoid hammering on rapid changes)
        debouncedFetch();
      }
    });
  }

  // Public refresh API — called by the home-spa-pdp section on SPA swaps so
  // the cached `handle` re-syncs with the element's `data-handle` attribute
  // (which is updated whenever the user changes products without a real nav).
  // Without this the bundle's init-time closure pins the handle to the seed
  // and stale reasons render for whatever product loaded first.
  function refreshFromDOM() {
    if (!targetEl) {
      targetEl = document.getElementById('convomesh-social-proof-text');
    }
    if (!targetEl) return;
    var newHandle = targetEl.getAttribute('data-handle');
    var newTenant = targetEl.getAttribute('data-tenant');
    if (!newHandle || !newTenant) return;
    // Reset visible state if handle changed so stale reasons don't linger
    if (newHandle !== handle) {
      targetEl.innerHTML = '';
      targetEl.style.display = 'none';
    }
    handle = newHandle;
    tenantId = newTenant;
    fetchReasons();
  }
  window.convomeshSocialProofRefresh = refreshFromDOM;
  // Also listen for a custom event so producers can fire-and-forget without
  // needing a direct function reference.
  window.addEventListener('convomesh-handle-changed', refreshFromDOM);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
